import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useNavigate } from 'react-router-dom';
import {
  reservationFormsApi,
  ReservationForm,
  PaginatedReservationFormsResponse,
} from '../services/reservationFormsService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const ReservationFormsListPage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError } = useSnackbar();
  const navigate = useNavigate();
  const [reservationForms, setReservationForms] = useState<ReservationForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Search and filter states
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [filterDate, setFilterDate] = useState<string | null>(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [tempDateFilter, setTempDateFilter] = useState('');

  const fetchReservationForms = async (pageNum: number = 1) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const params: any = {
        page: pageNum,
        limit,
      };

      // Add search filters
      if (searchName) {
        params.contactName = searchName;
      }
      if (searchEmail) {
        params.email = searchEmail;
      }
      if (searchPhone) {
        params.phone = searchPhone;
      }
      if (filterDate) {
        params.reservationDate = filterDate;
      }

      const response: PaginatedReservationFormsResponse =
        await reservationFormsApi.getReservationForms(params, sessionToken);

      setReservationForms(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch reservation forms';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservationForms();
  }, [sessionToken]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    fetchReservationForms(value);
  };

  const handleEdit = (id: number) => {
    navigate(`/reservation-form?id=${id}`);
  };

  const handleCreateNew = () => {
    navigate('/reservation-form');
  };

  const handleSearch = () => {
    setPage(1);
    fetchReservationForms(1);
  };

  const handleClearSearch = () => {
    setSearchName('');
    setSearchEmail('');
    setSearchPhone('');
    setFilterDate(null);
    setPage(1);
    fetchReservationForms(1);
  };

  const handleDateFilterOpen = () => {
    setTempDateFilter(filterDate || '');
    setDateDialogOpen(true);
  };

  const handleDateFilterClose = () => {
    setDateDialogOpen(false);
  };

  const handleDateFilterApply = () => {
    setFilterDate(tempDateFilter || null);
    setDateDialogOpen(false);
    setPage(1);
    fetchReservationForms(1);
  };

  const handleDateFilterClear = () => {
    setTempDateFilter('');
    setFilterDate(null);
    setDateDialogOpen(false);
    setPage(1);
    fetchReservationForms(1);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    // Handle both 12-hour and 24-hour formats
    const time24Match = timeString.match(/^(\d{1,2}):(\d{2})$/);
    if (time24Match) {
      const hours = parseInt(time24Match[1]);
      const minutes = time24Match[2];
      if (hours === 0) {
        return `12:${minutes} AM`;
      } else if (hours < 12) {
        return `${hours}:${minutes} AM`;
      } else if (hours === 12) {
        return `12:${minutes} PM`;
      } else {
        return `${hours - 12}:${minutes} PM`;
      }
    }
    // Already in 12-hour format
    return timeString;
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && reservationForms.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Reservation Forms
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchReservationForms(page)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Section */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            label="Search by Name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Contact name..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Search by Email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Email address..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Search by Phone"
            value={searchPhone}
            onChange={(e) => setSearchPhone(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Phone number..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={handleDateFilterOpen}
            sx={{ minWidth: 150 }}
          >
            {filterDate ? `Date: ${formatDate(filterDate)}` : 'Filter by Date'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          {(searchName || searchEmail || searchPhone || filterDate) && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearSearch}
            >
              Clear
            </Button>
          )}
        </Box>
        {(searchName || searchEmail || searchPhone || filterDate) && (
          <Typography variant="body2" color="text.secondary">
            Active filters: {searchName && `Name: ${searchName} `}
            {searchEmail && `Email: ${searchEmail} `}
            {searchPhone && `Phone: ${searchPhone} `}
            {filterDate && `Date: ${formatDate(filterDate)}`}
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Name', 'Email', 'Phone', 'Status', 'Reservation Date', 'Reservation Time', 'Actions'].map((label) => (
                  <TableCell
                    key={label}
                    align={label === 'Actions' ? 'center' : 'left'}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1.5,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reservationForms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {searchName || searchEmail || searchPhone || filterDate
                        ? 'No reservations found matching the search criteria'
                        : 'No reservations found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reservationForms.map((form) => (
                  <TableRow key={form.id} hover>
                    <TableCell>{form.contactName}</TableCell>
                    <TableCell>{form.email || '-'}</TableCell>
                    <TableCell>{formatPhoneNumber(form.phone) || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={form.status || 'Pending'}
                        color={getStatusColor(form.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(form.reservationDate)}</TableCell>
                    <TableCell>{formatTime(form.arrivalTime)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="secondary"
                        onClick={() => handleEdit(form.id)}
                        aria-label="edit"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {reservationForms.length} of {total} reservation
              {total !== 1 ? 's' : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      {/* Date Filter Dialog */}
      <Dialog open={dateDialogOpen} onClose={handleDateFilterClose}>
        <DialogTitle>Filter by Reservation Date</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Reservation Date"
            value={tempDateFilter}
            onChange={(e) => setTempDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDateFilterClose}>Cancel</Button>
          <Button onClick={handleDateFilterClear}>Clear</Button>
          <Button onClick={handleDateFilterApply} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationFormsListPage;

